/**
 * Application display name.
 *
 * Every UI surface that shows the app name should reference this constant.
 * "Azabu" remains the internal codename used in package metadata, storage keys,
 * and developer docs.
 */
export const APP_NAME = 'StitchPress';

/** Visible watermark drawn on every exported PNG (not user-configurable).
 *  'badge' = small logo + name in the bottom-right corner (pre-alpha default,
 *  unobtrusive, easy to crop — the invisible noise layer is the durable mark);
 *  'tile' = the name repeated diagonally across the whole image. */
export type ExportWatermarkStyle = 'badge' | 'tile';
export const EXPORT_WATERMARK_STYLE: ExportWatermarkStyle = 'badge';

/** Support / privacy contact address, shown on the privacy policy page. */
export const SUPPORT_EMAIL = 'support@kinnet.studio';

/** Help center origins, one per deploy environment. */
export const HELP_SITE_URLS = {
    development: 'https://dev.help.stitchpress.kinnet.studio',
    staging: 'https://staging.help.stitchpress.kinnet.studio',
    production: 'https://help.stitchpress.kinnet.studio',
} as const;

/** Marketing site (landing page + blog) origins, one per deploy environment. */
export const LANDING_SITE_URLS = {
    development: 'https://dev.stitchpress.kinnet.studio',
    staging: 'https://staging.stitchpress.kinnet.studio',
    production: 'https://stitchpress.kinnet.studio',
} as const;

export type DeployTier = keyof typeof HELP_SITE_URLS;
type DeployEnv = { VITE_DEPLOY_ENV?: string; MODE?: string };

/**
 * Resolve the current deploy environment. Mirrors the Sentry/Statsig env
 * resolution: prefer the CI-injected deploy env, else Vite's build mode (so
 * local `bun run dev` links to the dev sites). Unknown tiers fall back to
 * production — a public-safe default.
 */
export function resolveDeployTier(env: DeployEnv = import.meta.env): DeployTier {
    const tier = env.VITE_DEPLOY_ENV?.trim() || env.MODE;
    if (tier === 'development' || tier === 'staging') return tier;
    return 'production';
}

/** Help center origin for the current deploy environment. */
export function resolveHelpSiteUrl(env: DeployEnv = import.meta.env): string {
    return HELP_SITE_URLS[resolveDeployTier(env)];
}

/** Marketing site origin for the current deploy environment. */
export function resolveLandingSiteUrl(env: DeployEnv = import.meta.env): string {
    return LANDING_SITE_URLS[resolveDeployTier(env)];
}
